
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { Breakpoint } from "@/types/breakpoints";
import { configStore } from "@/store/store";
import {
  mediaQueryString,
  validateBreakpoint
} from "@/lib/breakpoints/breakpoints";
import defensiveCopy from "@/util/defensive-copy";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import IconButton from "@/components/UI/IconButton.vue";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import ConfirmDialog from "@/components/UI/ConfirmDialog.vue";

@Component({
  components: {
    IconButton,
    TextInput,
    NumberInput,
    DropdownInput,
    NiceButton,
    ConfirmDialog
  },
  directives: {
    focus: {
      // focus element on mount
      inserted: function (el) {
        el.focus();
      }
    }
  }
})
export default class EditBreakpoint extends Vue {
  @Prop()
  value!: Breakpoint;
  @Prop()
  isEditing!: boolean;

  deleteDialog = false;

  internalValue = defensiveCopy(this.value);

  setDefaultPreviewWidth(width: "100%" | "custom") {
    // the Breakpoint defaultPreviewWidth property must be a number or "100%",
    // so we need to set a default value if width is "custom"
    const defaultPreviewWidth =
      this.internalValue.maxWidth ?? this.internalValue.minWidth ?? 250;
    this.internalValue.defaultPreviewWidth =
      width === "100%" ? "100%" : defaultPreviewWidth;
  }

  enableEditing() {
    this.$emit("edit", this.value.id);
  }

  get breakpoints() {
    return configStore.breakpoints;
  }

  get previewWidthUnit() {
    return "px";
  }

  deleteBreakpoint() {
    this.$emit("delete");
    this.deleteDialog = false;
  }

  get mediaQueryString() {
    const minWidth = this.internalValue.minWidth || undefined;
    const maxWidth = this.internalValue.maxWidth || undefined;

    return mediaQueryString(minWidth, maxWidth);
  }

  cancel() {
    this.internalValue = defensiveCopy(this.value);
    this.$emit("edit", "");
  }

  save() {
    const newBreakpoint = defensiveCopy(this.internalValue);
    pipe(
      validateBreakpoint(newBreakpoint),
      fold(
        errors => {
          errors.forEach(e => NotificationLogger.log.message(e.msg));
        },
        breakpoint => {
          this.$emit("input", breakpoint);
          this.$emit("edit", "");
        }
      )
    );
  }
}
